import type { ChatMessage, Plan } from './data';
import type { OwnerCommand } from './data';
import type { ObserverMessageRow } from './observer-view';
import type { MainCommandReplyTarget } from './main-command-recipients';
import type { SupabaseClient } from '@supabase/supabase-js';

export type MainCommandDeliveryState =
  | 'accepted' | 'waiting' | 'queued' | 'busy' | 'claimed' | 'replied'
  | 'offline' | 'unavailable' | 'not_configured'
  | 'unsupported' | 'failed' | 'skipped';

export interface MainCommandRecipientStatus {
  readonly key: string;
  readonly label: string;
  readonly state: MainCommandDeliveryState;
  readonly text: string;
  readonly terminal: boolean;
}

export interface MainCommandDeliveryRecord {
  readonly label?: string;
  readonly participant?: string;
  readonly role?: string;
  readonly target?: string;
  readonly workerId?: string;
  readonly worker_id?: string;
  readonly state?: string;
  readonly status?: string;
  readonly reasonCode?: string;
  readonly reason_code?: string;
  readonly reason?: string;
}

export interface MainCommandDeliverySnapshot {
  readonly commandId: string;
  readonly managementDeliveries: readonly MainCommandDeliveryRecord[];
  readonly workerDeliveries: readonly MainCommandDeliveryRecord[];
}

export interface MainCommandAcceptedEcho {
  readonly commandId: string;
  readonly requestId: string;
  readonly seq: number | null;
  readonly text: string;
  readonly targets: readonly MainCommandReplyTarget[];
  readonly workerIds: readonly string[];
  readonly managementDeliveries: readonly MainCommandDeliveryRecord[];
  readonly workerDeliveries: readonly MainCommandDeliveryRecord[];
  /** What Brunel said the message became: 'job' (planned) or 'steering'. Absent when it did not say. */
  readonly routedAs?: string | null;
  /** For a job that addresses a Worker, that Worker's number. */
  readonly jobWorkerNumber?: number | null;
}

export interface MainCommandReceiptSegment {
  readonly state: MainCommandDeliveryState;
  readonly symbol: string;
  readonly label: string;
  readonly count: number;
  readonly tone: 'persisted' | 'queued' | 'active' | 'replied' | 'failed';
}

function deliveryRecords(value: unknown): readonly MainCommandDeliveryRecord[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is MainCommandDeliveryRecord =>
    item !== null && typeof item === 'object' && !Array.isArray(item));
}

export function mainCommandDeliverySnapshot(value: unknown): MainCommandDeliverySnapshot | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const result = value as Record<string, unknown>;
  if (result['ok'] !== true || result['value'] === null
    || typeof result['value'] !== 'object' || Array.isArray(result['value'])) return null;
  const body = result['value'] as Record<string, unknown>;
  if (typeof body['commandId'] !== 'string' || body['commandId'] === '') return null;
  return {
    commandId: body['commandId'],
    managementDeliveries: deliveryRecords(body['managementDeliveries']),
    workerDeliveries: deliveryRecords(body['workerDeliveries']),
  };
}

export async function loadMainCommandDeliveryState(
  client: SupabaseClient,
  orgId: string,
  commandId: string,
): Promise<MainCommandDeliverySnapshot | null> {
  const response = await client.rpc('main_command_delivery_state', {
    request: { orgId, commandId },
  });
  if (response.error !== null) return null;
  return mainCommandDeliverySnapshot(response.data);
}

/**
 * A successful local echo remains visible only until either of its canonical
 * persistence identities has appeared in the owner-command read.
 */
export function unreconciledMainCommandEchoes(
  commands: readonly Pick<OwnerCommand, 'id' | 'request_id'>[],
  accepted: readonly MainCommandAcceptedEcho[],
): readonly MainCommandAcceptedEcho[] {
  const commandIds = new Set(commands.map((command) => command.id));
  const requestIds = new Set(commands.flatMap((command) =>
    command.request_id ? [command.request_id] : []));
  return accepted.filter((echo) =>
    !commandIds.has(echo.commandId) && !requestIds.has(echo.requestId));
}

function identityCommandId(identity: unknown): string | null {
  if (identity === null || typeof identity !== 'object' || Array.isArray(identity)) return null;
  const value = (identity as Record<string, unknown>)['commandId'];
  return typeof value === 'string' && value !== '' ? value : null;
}

function normalized(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/[ -]+/g, '_') : '';
}

function stateOf(record: MainCommandDeliveryRecord | undefined, worker: boolean): MainCommandDeliveryState {
  if (record === undefined) return 'accepted';
  const raw = normalized(record?.state ?? record?.status);
  const reason = normalized(record?.reasonCode ?? record?.reason_code ?? record?.reason);
  switch (raw) {
    case 'replied':
    case 'reply':
    case 'complete':
    case 'completed':
      return 'replied';
    case 'offline': return 'offline';
    case 'unavailable': return 'unavailable';
    case 'not_configured':
    case 'notconfigured': return 'not_configured';
    case 'unsupported': return 'unsupported';
    case 'failed':
    case 'error': return 'failed';
    case 'skipped':
      if (reason === 'node_offline') return 'offline';
      if (reason === 'no_conversational_session' || reason === 'worker_retired'
        || reason === 'no_live_conversation') return 'unavailable';
      return 'skipped';
    case 'busy':
    case 'wait': return 'busy';
    case 'claimed':
    case 'started':
    case 'active':
    case 'in_progress': return 'claimed';
    case 'queued':
    case 'delivered': return 'queued';
    case 'accepted':
    case 'persisted': return 'accepted';
    case 'waiting':
    case 'pending':
    case '': return 'waiting';
    default: return worker ? 'queued' : 'waiting';
  }
}

function humanReason(record: MainCommandDeliveryRecord | undefined): string | null {
  const raw = record?.reasonCode ?? record?.reason_code ?? record?.reason;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  return raw.trim().replace(/[_-]+/g, ' ');
}

function stateText(state: MainCommandDeliveryState, reason: string | null): string {
  const base = state === 'not_configured' ? 'Not configured'
    : state === 'replied' ? 'Replied'
      : state === 'claimed' ? 'Claimed / started'
      : state === 'accepted' ? 'Accepted / persisted'
      : state === 'waiting' ? 'Waiting'
        : state === 'queued' ? 'Queued'
          : state === 'busy' ? 'Busy — waiting'
            : state === 'offline' ? 'Offline — skipped'
              : state === 'unavailable' ? 'Unavailable'
                : state === 'unsupported' ? 'Unsupported — skipped'
                  : state === 'failed' ? 'Failed'
                    : 'Skipped';
  if (reason === null) return base;
  const lower = base.toLowerCase();
  return lower.includes(reason.toLowerCase()) ? base : `${base} · ${reason}`;
}

const RECEIPT_ORDER: readonly MainCommandDeliveryState[] = [
  'accepted', 'waiting', 'queued', 'busy', 'claimed', 'replied',
  'offline', 'unavailable', 'not_configured', 'unsupported', 'failed', 'skipped',
];

function receiptVisual(state: MainCommandDeliveryState): Omit<MainCommandReceiptSegment, 'state' | 'count'> {
  if (state === 'accepted' || state === 'waiting') {
    return { symbol: 'P', label: state === 'accepted' ? 'Accepted and persisted' : 'Persisted; awaiting delivery state', tone: 'persisted' };
  }
  if (state === 'queued' || state === 'busy') {
    return { symbol: 'Q', label: state === 'queued' ? 'Delivered or queued' : 'Queued behind current work', tone: 'queued' };
  }
  if (state === 'claimed') return { symbol: '▶', label: 'Participant claimed or started', tone: 'active' };
  if (state === 'replied') return { symbol: '↩', label: 'Participant replied', tone: 'replied' };
  return {
    symbol: '!',
    label: state === 'offline' ? 'Offline; delivery skipped'
      : state === 'unavailable' ? 'Unavailable; delivery skipped'
        : state === 'not_configured' ? 'Not configured; delivery skipped'
          : state === 'unsupported' ? 'Unsupported; delivery skipped'
            : state === 'failed' ? 'Delivery failed'
              : 'Delivery skipped',
    tone: 'failed',
  };
}

/** Compact aggregate over the same per-recipient canonical states shown in detail. */
export function mainCommandReceiptSegments(
  statuses: readonly MainCommandRecipientStatus[],
): readonly MainCommandReceiptSegment[] {
  const counts = new Map<MainCommandDeliveryState, number>();
  for (const status of statuses) counts.set(status.state, (counts.get(status.state) ?? 0) + 1);
  return RECEIPT_ORDER.flatMap((state) => {
    const count = counts.get(state) ?? 0;
    if (count === 0) return [];
    return [{ state, count, ...receiptVisual(state) }];
  });
}

function terminal(state: MainCommandDeliveryState): boolean {
  return state === 'replied' || state === 'offline' || state === 'unavailable'
    || state === 'not_configured' || state === 'unsupported'
    || state === 'failed' || state === 'skipped';
}

function managementKey(record: MainCommandDeliveryRecord): string {
  return normalized(record.participant ?? record.role ?? record.target);
}

function deliveryForManagement(
  records: readonly MainCommandDeliveryRecord[],
  role: MainCommandReplyTarget,
): MainCommandDeliveryRecord | undefined {
  const aliases = role === 'overseer' ? new Set(['overseer','main_overseer','brunel'])
    : role === 'ux' ? new Set(['ux','observer_ux'])
      : new Set(['core','observer_core']);
  return records.find((record) => aliases.has(managementKey(record)));
}

function deliveryForWorker(
  records: readonly MainCommandDeliveryRecord[],
  workerId: string,
): MainCommandDeliveryRecord | undefined {
  return records.find((record) => (record.workerId ?? record.worker_id ?? record.participant) === workerId);
}

function observerReplyState(
  commandId: string,
  role: 'ux' | 'core' | 'overseer',
  messages: readonly ObserverMessageRow[],
): MainCommandDeliveryState | null {
  const message = [...messages].reverse().find((row) =>
    row.author_role === role && identityCommandId(row.execution_identity) === commandId);
  if (!message) return null;
  // #644: an executed reply is identified by its node-reported identity, not its wording. A
  // grounded answer such as "the MacBook is offline" is still a reply; only a rejected
  // (mismatched) execution fails. Failure notices carry no actual runtime and fall through.
  const identity = message.execution_identity !== null && typeof message.execution_identity === 'object'
    && !Array.isArray(message.execution_identity) ? message.execution_identity as Record<string, unknown> : {};
  if (identity['mismatch'] === true) return 'failed';
  if (typeof identity['actualRuntime'] === 'string' && identity['actualRuntime'].trim() !== '') return 'replied';
  const body = message.body.toLowerCase();
  if (body.includes('not_configured') || body.includes('not configured')) return 'not_configured';
  if (body.includes('offline')) return 'offline';
  if (body.includes('unavailable')) return 'unavailable';
  if (body.includes('unsupported')) return 'unsupported';
  if (body.includes('failed')) return 'failed';
  return 'replied';
}

/**
 * #644: the commands whose Overseer answer is a conversation reply - one it gave, or one it is
 * still claiming or queued for - rather than a plan. For these the screen never puts its own
 * computed line in the Overseer's mouth.
 */
export function overseerConversationCommands(input: {
  readonly observerMessages: readonly ObserverMessageRow[];
  readonly deliveries: Iterable<{ readonly commandId: string; readonly managementDeliveries?: readonly MainCommandDeliveryRecord[] }>;
}): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const row of input.observerMessages) {
    if (row.author_role !== 'overseer' || row.discussion_id != null) continue;
    const commandId = identityCommandId(row.execution_identity);
    if (commandId !== null) ids.add(commandId);
  }
  for (const snapshot of input.deliveries) {
    const record = deliveryForManagement(snapshot.managementDeliveries ?? [], 'overseer');
    const reason = record?.reasonCode ?? record?.reason_code ?? record?.reason;
    if (typeof reason === 'string' && reason.startsWith('overseer_')) ids.add(snapshot.commandId);
  }
  return ids;
}

export function mainCommandRecipientStatuses(input: {
  readonly commandId: string;
  readonly targets: readonly MainCommandReplyTarget[];
  readonly workerIds: readonly string[];
  readonly workerLabels?: Readonly<Record<string, string>>;
  readonly managementDeliveries?: readonly MainCommandDeliveryRecord[];
  readonly workerDeliveries?: readonly MainCommandDeliveryRecord[];
  readonly plans?: readonly Plan[];
  readonly observerMessages?: readonly ObserverMessageRow[];
  readonly workerMessages?: readonly ChatMessage[];
}): readonly MainCommandRecipientStatus[] {
  const managementDeliveries = input.managementDeliveries ?? [];
  const workerDeliveries = input.workerDeliveries ?? [];
  const plans = input.plans ?? [];
  const observerMessages = input.observerMessages ?? [];
  const workerMessages = input.workerMessages ?? [];
  const statuses: MainCommandRecipientStatus[] = [];

  for (const role of input.targets) {
    const explicit = deliveryForManagement(managementDeliveries, role);
    let state = stateOf(explicit, false);
    if (role === 'overseer' && plans.some((plan) => plan.command_id === input.commandId)) state = 'replied';
    // #644: a conversation turn to the Overseer is answered by the Overseer's own reply, not a plan.
    if (role === 'overseer') {
      const observed = observerReplyState(input.commandId, 'overseer', observerMessages);
      if (observed === 'replied' || (observed !== null && explicit === undefined)) state = observed;
    }
    if (role === 'ux' || role === 'core') {
      const observed = observerReplyState(input.commandId, role, observerMessages);
      // The #553 RPC carries the exact unsupported/offline/unavailable route
      // reason. A later real reply may advance it to replied; a prose fallback
      // must not downgrade that canonical distinction.
      if (observed === 'replied' || (observed !== null && explicit === undefined)) state = observed;
    }
    const label = explicit?.label
      ?? (role === 'overseer' ? 'Overseer' : role === 'ux' ? 'Observer UX' : 'Observer Core');
    statuses.push({
      key: role,
      label,
      state,
      text: stateText(state, state === 'replied' ? null : humanReason(explicit)),
      terminal: terminal(state),
    });
  }

  for (const workerId of input.workerIds) {
    const explicit = deliveryForWorker(workerDeliveries, workerId);
    let state = stateOf(explicit, true);
    if (workerMessages.some((message) =>
      message.author_kind === 'worker' && message.owner_command_id === input.commandId
      && message.worker_id === workerId)) {
      state = 'replied';
    }
    statuses.push({
      key: `worker:${workerId}`,
      label: explicit?.label ?? input.workerLabels?.[workerId] ?? 'Worker',
      state,
      text: stateText(state, state === 'replied' ? null : humanReason(explicit)),
      terminal: terminal(state),
    });
  }

  return statuses;
}

export function allMainCommandRecipientsSettled(statuses: readonly MainCommandRecipientStatus[]): boolean {
  return statuses.length > 0 && statuses.every((status) => status.terminal);
}
